import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";

const SAFE_NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

@customElement("create-instance-dialog")
export class CreateInstanceDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;
  @property() tenant = "";

  @state() private _name = "";
  @state() private _applying = false;
  @state() private _error = "";

  private get _isValid(): boolean {
    return SAFE_NAME_PATTERN.test(this._name);
  }

  private _close() {
    this._name = "";
    this._error = "";
    this._applying = false;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (!this._isValid || this._applying) return;
    this._applying = true;
    this._error = "";
    try {
      await api.createInstance({
        tenant: this.tenant,
        name: this._name.trim(),
      });
      this.dispatchEvent(new CustomEvent("instance-created", { detail: { name: this._name.trim() } }));
      this._close();
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create instance";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <bee-modal open in-root title="Add Instance — ${this.tenant}" @bee-request-close=${this._close}>
        <div class="form-field">
          <label>Instance Name</label>
          <bee-input
            placeholder="e.g., production"
            .value=${this._name}
            @bee-input=${(e: Event) => { this._name = (e.target as HTMLInputElement).value.trim().toLowerCase(); }}
          ></bee-input>
        </div>

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating..." : "Add Instance"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
